import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Facebook, Instagram } from 'lucide-react';
import { companyInfo } from '../utils/mockData';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const handleManageCookies = () => {
    window.dispatchEvent(new CustomEvent('openCookieBanner'));
  };

  return (
    <footer className="bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_edil-service/artifacts/nsw2ji7s_IMG_1877.png" 
                alt="Edil Service 2.0" 
                className="h-18 w-auto"
              />
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              {companyInfo.slogan}
            </p>
            <div className="flex gap-3 mt-4">
              <a 
                href="https://www.instagram.com/francesco_valerio_edilvale" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-600 hover:bg-blue-600 flex items-center justify-center transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
              <a 
                href="https://www.facebook.com/francesco_valerio_edilvale" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-600 hover:bg-blue-600 flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-orange-400">Link Rapidi</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-slate-300 hover:text-orange-400 transition-colors text-sm">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/chi-siamo" className="text-slate-300 hover:text-orange-400 transition-colors text-sm">
                  Chi Siamo
                </Link>
              </li>
              <li>
                <Link to="/prodotti" className="text-slate-300 hover:text-orange-400 transition-colors text-sm">
                  Prodotti
                </Link>
              </li>
              <li>
                <Link to="/servizi" className="text-slate-300 hover:text-orange-400 transition-colors text-sm">
                  Servizi
                </Link>
              </li>
              <li>
                <Link to="/marchi" className="text-slate-300 hover:text-orange-400 transition-colors text-sm">
                  Marchi Trattati
                </Link>
              </li>
              <li>
                <Link to="/contatti" className="text-slate-300 hover:text-orange-400 transition-colors text-sm">
                  Contatti
                </Link>
              </li>
              <li className="pt-2 border-t border-slate-600">
                <Link to="/admin/login" className="text-blue-400 hover:text-blue-300 transition-colors text-sm font-semibold">
                  🔐 Area Amministratore
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-orange-400">Contatti</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-orange-400 mt-1 flex-shrink-0" />
                <span className="text-slate-300 text-sm">{companyInfo.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-orange-400 flex-shrink-0" />
                <a href={`tel:${companyInfo.phone}`} className="text-slate-300 hover:text-orange-400 transition-colors text-sm">
                  {companyInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-orange-400 flex-shrink-0" />
                <a href={`mailto:${companyInfo.email}`} className="text-slate-300 hover:text-orange-400 transition-colors text-sm">
                  {companyInfo.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Opening Hours */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-orange-400">Orari di Apertura</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Clock size={18} className="text-orange-400 mt-1 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-slate-300 font-medium">Lunedì - Venerdì</p>
                  <p className="text-slate-400">07:00 - 12:30</p>
                  <p className="text-slate-400">14:00 - 17:30</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={18} className="text-orange-400 mt-1 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-slate-300 font-medium">Sabato</p>
                  <p className="text-slate-400">07:00 - 13:00</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={18} className="text-orange-400 mt-1 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-slate-300 font-medium">Domenica</p>
                  <p className="text-slate-400">Chiuso</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-600 mt-12 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-400">
            <p>© {currentYear} Edil Service 2.0 SRL. Tutti i diritti riservati.</p>
            <div className="flex flex-wrap gap-4 md:gap-6 justify-center">
              <Link to="/privacy-policy" className="hover:text-orange-400 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/cookie-policy" className="hover:text-orange-400 transition-colors">
                Cookie Policy
              </Link>
              <Link to="/termini-e-condizioni" className="hover:text-orange-400 transition-colors">
                Termini e Condizioni
              </Link>
              <button 
                onClick={handleManageCookies}
                className="hover:text-orange-400 transition-colors underline cursor-pointer"
              >
                Gestisci cookie
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
